<template>
  <InfiniteScroll v-if="shipments.length" :shipments="shipments" :next-offset="nextOffset" @load="loadPurchases">
    <template #default="{ shipment }">
      <PurchaseCarton :shipment="shipment" group-items />
    </template>
  </InfiniteScroll>
  <div v-else-if="showSkeleton" data-testid="skeleton-container" :class="{ 'margin-top-xs': showInitialSkeleton }">
    <div v-for="index in 4" :key="index" class="margin-top-xxs margin-right margin-bottom-xxs margin-left">
      <Skeleton width="100%" height="172px" />
    </div>
  </div>
  <!-- Empty state -->
  <template v-else-if="showNoOrders">
    <div
      class="grid-x no-orders align-center"
      :class="[
        'small-margin-top-xs medium-margin-top-s small-margin-bottom-l medium-margin-bottom-m',
        'small-padding-top-m small-padding-bottom-m medium-padding-top-l medium-padding-bottom-l',
      ]"
    >
      <div class="cell medium-7 text-center" role="alert" aria-live="polite">
        {{ noCardInWallet ? noCardInWalletMessage : noOrdersMessage }}
      </div>
      <a v-if="noCardInWallet" href="/account/wallet?ocwallet=true" class="small-11 medium-6 button button-small primary margin-top-m">
        {{ addCardToWalletLabel }}
      </a>
    </div>
  </template>
</template>

<script setup>
import { computed, onUnmounted, watch, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { Cookie } from '@vue-util/cookie';
import { fireTag } from '@vue-util/tagManager';
import Skeleton from '@atomic-ui/skeleton/Skeleton';
import PurchaseCarton from '@/components/ppe/PurchaseCarton';
import InfiniteScroll from '@/components/common/InfiniteScroll';
import { ORDER_HISTORY, onlineUid, PPE_ERROR_TAG } from 'config';
import { useInstoreOrderHistoryStore } from '@/stores/ppe/useInstoreOrderHistoryStore';
import { useOrderHistoryStore } from '@/stores/ppe/useOrderHistoryStore';

const route = useRoute();
const instoreOrderHistoryStore = useInstoreOrderHistoryStore();
const orderHistoryStore = useOrderHistoryStore();

const shipments = computed(() => instoreOrderHistoryStore.shipments);
const nextOffset = computed(() => instoreOrderHistoryStore.nextOffset);
const noCardInWallet = computed(() => instoreOrderHistoryStore.noCardInWallet);
const error = computed(() => instoreOrderHistoryStore.error);
const loading = computed(() => instoreOrderHistoryStore.isLoading);
const showSkeleton = computed(() => loading.value && !error.value);
const showNoOrders = computed(() => !loading.value && !shipments.value.length && !error.value);
const showInitialSkeleton = computed(() => showSkeleton.value && !shipments.value.length);
const selectedDate = computed(() => orderHistoryStore.selectedDate);
const { inStoreHistory } = ORDER_HISTORY;
const { routeName, inStoreHistoryTag, noOrdersMessage, addCardToWalletLabel, noCardInWalletMessage } = inStoreHistory;

const getPurchases = async (payload) => {
  await instoreOrderHistoryStore.GET_INSTORE_ORDER_HISTORY(payload);
};
const clearStore = async () => {
  await instoreOrderHistoryStore.CLEAR_INSTORE_ORDER_HISTORY();
};
const loadPurchases = async (dateFilterUpdated = false) => {
  const selectedDateRange = dateFilterUpdated ? route.query.daterange : selectedDate.value[routeName]?.dateRange;
  await getPurchases({ ...(selectedDateRange && { dateRange: selectedDateRange }), userToken: Cookie.get(onlineUid), dateFilterUpdated });
  if (error.value) {
    fireTag('link', {
      ...PPE_ERROR_TAG.error,
      error_code: [error.value.errorCode || PPE_ERROR_TAG.defaultErrorCode],
      error_message: [error.value.message],
    });
  }
};
watch(
  () => route.query.daterange,
  () => {
    if (route.name !== routeName) return;
    loadPurchases(true);
  },
);
onMounted(() => {
  loadPurchases();
  fireTag('view', {
    page_type: inStoreHistoryTag.page_type,
    customer_type: inStoreHistoryTag.customer_type,
    active_order: 'false',
    page_name: inStoreHistoryTag.page_name,
  });
});
onUnmounted(() => {
  clearStore();
});
</script>

<style lang="scss" scoped>
.no-orders {
  background-color: $background-color;
}
</style>
